import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import InDepthArticle from "@/components/InDepthArticle";
import SIRawCurrentAffairs from "@/components/SIRawCurrentAffairs";
import { useExam } from "@/context/ExamContext";
import { useTrackSection } from "@/hooks/useTrackSection";
import useHeartbeat from "@/hooks/useHeartbeat";
import {
  ArrowLeft, MapPin, User, BookOpen, ChevronDown, ChevronRight,
  Target, FileText, CheckCircle2, Clock,
  Newspaper, Globe, Users, GraduationCap,
  RefreshCw, Star, Building2, Scale, Leaf, Briefcase, Shield, Microscope, 
  Download, Calendar, Flame, AlertCircle, Landmark
} from "lucide-react";

/**
 * CLARIAS Daily Intelligence V2 - VisionIAS-Level Current Affairs
 * Premium institute-quality content for UPSC preparation
 * Now with PIB section and all download options
 */

/**
 * DeepCollapsible — collapsed-by-default wrapper for deep analytical sub-sections.
 * Per IAS officer feedback: reduce cognitive overload by hiding heavy analysis
 * behind a header the student can expand on demand.
 */
const DeepCollapsible = ({ title, icon: Icon, containerClass, headerClass, iconClass, testId, defaultOpen = false, children }) => {
  // Initial state seeded from defaultOpen. Note: since CollapsibleContent unmounts
  // when its parent news card is collapsed (Radix Presence), this component
  // remounts on reopen and picks up the latest defaultOpen value automatically.
  // That's how reading-mode changes propagate without a useEffect re-sync.
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={containerClass}>
        <CollapsibleTrigger
          data-testid={testId}
          className={`w-full flex items-center justify-between gap-2 text-left ${headerClass}`}
        >
          <span className="font-semibold flex items-center gap-2 text-sm sm:text-base">
            {Icon && <Icon className={`w-4 h-4 ${iconClass || ''}`} />}
            {title}
          </span>
          {open ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-70" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0 opacity-70" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          {children}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const DailyIntelligenceV2Page = () => {
  const navigate = useNavigate();
  const { isSI } = useExam();
  useTrackSection();
  useHeartbeat({ activity: "daily_intel", module: "current_affairs" });
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pibData, setPibData] = useState([]);
  const [pibLoading, setPibLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState("summary");
  const [expandedItems, setExpandedItems] = useState({});
  const [filterPaper, setFilterPaper] = useState("all");
  const [downloading, setDownloading] = useState(null);
  // Progressive Disclosure: reading mode (persisted across sessions)
  const [readingMode, setReadingMode] = useState(() => {
    try { return localStorage.getItem("ci_reading_mode") || "quick"; } catch (e) { return "quick"; }
  });
  useEffect(() => {
    try { localStorage.setItem("ci_reading_mode", readingMode); } catch (e) { /* localStorage may be unavailable */ }
  }, [readingMode]);
  // Mark-as-read: derived from localStorage via a version counter (avoids setState-in-effect)
  const [readVersion, setReadVersion] = useState(0);
  const readItems = useMemo(() => {
    try {
      const raw = localStorage.getItem(`ci_read_${selectedDate}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (e) { return new Set(); }
  }, [selectedDate, readVersion]);
  const toggleRead = useCallback((id) => {
    const next = new Set(readItems);
    if (next.has(id)) next.delete(id); else next.add(id);
    try { localStorage.setItem(`ci_read_${selectedDate}`, JSON.stringify([...next])); } catch (e) { /* ignore */ }
    // Maintain reading streak: counts today as "read-active" if at least one item is marked
    const todayIso = new Date().toISOString().split('T')[0];
    if (next.size > 0 && selectedDate === todayIso) {
      try {
        const last = localStorage.getItem("ci_streak_last_day");
        if (last !== todayIso) {
          const streak = parseInt(localStorage.getItem("ci_streak_count") || "0", 10);
          const y = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          const newStreak = (last === y) ? streak + 1 : 1;
          localStorage.setItem("ci_streak_count", String(newStreak));
          localStorage.setItem("ci_streak_last_day", todayIso);
        }
      } catch (e) { /* ignore */ }
    }
    setReadVersion(v => v + 1);
  }, [readItems, selectedDate]);
  const streakCount = (() => {
    try { return parseInt(localStorage.getItem("ci_streak_count") || "0", 10); } catch (e) { return 0; }
  })();
  // Mode → which "level" sections are open by default
  // level "essential" = always shown (Summary, Facts) - rendered as plain divs (not collapsible)
  // level "standard" = open in standard+deep, collapsed in quick
  // level "deep"     = open only in deep
  const isOpen = (level) => {
    if (level === "essential") return true;
    if (level === "standard") return readingMode !== "quick";
    return readingMode === "deep";
  };

  useEffect(() => { 
    fetchDailyIntelligence(); 
    fetchPIB();
  }, [selectedDate]);

  const fetchDailyIntelligence = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data: response } = await managedAxios.get(
        `${API_BASE}/intelligence/v2/daily?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPIB = useCallback(async () => {
    setPibLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data: response } = await managedAxios.get(
        `${API_BASE}/intelligence/pib/daily?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPibData(response.releases || []);
    } catch (err) {
      console.error("PIB fetch error:", err);
      setPibData([]);
    } finally {
      setPibLoading(false);
    }
  }, [selectedDate]);

  const downloadDailyPDF = useCallback(async () => {
    setDownloading('daily');
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/intelligence/v2/pdf/daily?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("PDF not available");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLARIAS_Daily_Intelligence_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("PDF download failed. Content may not be available for this date.");
    } finally {
      setDownloading(null);
    }
  }, [selectedDate]);

  const downloadWeeklyPDF = async () => {
    setDownloading('weekly');
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/intelligence/v2/pdf/weekly?end_date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Weekly PDF not available");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLARIAS_Weekly_Intelligence_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Weekly PDF download failed.");
    } finally {
      setDownloading(null);
    }
  };

  const downloadItemPDF = async (item) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/intelligence/v2/pdf/item/${item.id}?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("PDF not available");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTitle = (item.headline || 'item').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `CLARIAS_${selectedDate}_${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("PDF download failed. Please try again.");
    }
  };

  const toggleExpand = (id) => setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));

  const getDimensionIcon = (dim) => {
    const icons = { "Political": Scale, "Economic": Briefcase, "Social": Users, "Administrative": Building2, "Environmental": Leaf, "Legal": Scale, "International": Globe, "Scientific": Microscope, "Ethical": Shield };
    return icons[dim] || FileText;
  };

  const getPaperColor = (paper) => {
    const colors = { "GS1": "from-orange-500 to-red-500", "GS2": "from-blue-500 to-indigo-500", "GS3": "from-emerald-500 to-teal-500", "GS4": "from-purple-500 to-pink-500", "International": "from-sky-500 to-cyan-500", "Reports": "from-amber-500 to-orange-500" };
    return colors[paper] || "from-slate-500 to-slate-600";
  };

  // Universal safe renderer — handles string, array, dict from Gemini without crashing
  const safeText = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val.map(v => typeof v === 'string' ? v : JSON.stringify(v)).join('\n');
    if (typeof val === 'object') return Object.entries(val).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n');
    return String(val);
  };

  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return [val];
    if (typeof val === 'object') return Object.entries(val).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
    return [String(val)];
  };

  // Renders structured content: handles string (old), array of strings, or object with sub-sections
  const renderStructured = (val, textClass = "text-xs sm:text-sm text-slate-600") => {
    if (!val) return null;
    // String — render as paragraph (old format)
    if (typeof val === 'string') return <p className={`${textClass} leading-relaxed whitespace-pre-line`}>{val}</p>;
    // Array of strings — render as bullet list
    if (Array.isArray(val)) return (
      <ul className="space-y-1.5">
        {val.map((item, i) => (
          <li key={i} className={`${textClass} flex items-start gap-2`}>
            <span className="text-slate-400 mt-1 flex-shrink-0">•</span>
            <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
          </li>
        ))}
      </ul>
    );
    // Object with sub-sections — render each key as a labeled section with bullet points
    if (typeof val === 'object') return (
      <div className="space-y-3">
        {Object.entries(val).map(([key, subVal]) => (
          <div key={key}>
            <h5 className="font-semibold text-slate-700 text-xs sm:text-sm capitalize mb-1">{key.replace(/_/g, ' ')}</h5>
            {typeof subVal === 'string' ? (
              <p className={`${textClass} leading-relaxed pl-3`}>{subVal}</p>
            ) : Array.isArray(subVal) ? (
              <ul className="space-y-1 pl-3">
                {subVal.map((item, i) => (
                  <li key={i} className={`${textClass} flex items-start gap-2`}>
                    <span className="text-slate-400 mt-1 flex-shrink-0">•</span>
                    <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`${textClass} leading-relaxed pl-3`}>{JSON.stringify(subVal)}</p>
            )}
          </div>
        ))}
      </div>
    );
    return <p className={textClass}>{String(val)}</p>;
  };

  const filteredNews = data?.news_items?.filter(item => {
    if (filterPaper === "all") return true;
    if (filterPaper === "International") return item.category === "international";
    if (filterPaper === "Reports") return item.is_report === true;
    return item.syllabus?.gs_paper === filterPaper;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-indigo-400 animate-spin" />
      </div>
    );
  }

  // SI / State track: show the full raw scraped feed (60-100 items) instead of
  // the curated UPSC deep-analysis feed.
  if (isSI) {
    return <SIRawCurrentAffairs />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => navigate("/dashboard")} className="p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-xl" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
                  <Newspaper className="w-5 h-5 sm:w-7 sm:h-7 text-indigo-400" />Daily Intelligence
                </h1>
                <p className="text-indigo-300 text-xs sm:text-sm">Institute-Quality Current Affairs</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="bg-white/10 border border-white/20 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm flex-1 sm:flex-none" data-testid="date-picker" />
              <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white text-xs sm:text-sm" onClick={() => { fetchDailyIntelligence(); fetchPIB(); }}>
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          {data?.stats && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
              {[
                { val: data.stats.total_items || 0, label: "News", color: "text-white" },
                { val: data.stats.high_priority || 0, label: "High Priority", color: "text-red-400" },
                { val: data.stats.places_count || 0, label: "Places", color: "text-amber-400" },
                { val: data.stats.personalities_count || 0, label: "People", color: "text-emerald-400" },
                { val: pibData.length, label: "PIB", color: "text-cyan-400" },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-2 sm:p-3 text-center">
                  <p className={`text-lg sm:text-2xl font-bold ${s.color}`}>{s.val}</p>
                  <p className="text-[10px] sm:text-xs text-indigo-300">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Download Buttons */}
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
            <Button 
              onClick={downloadDailyPDF} 
              disabled={downloading === 'daily'}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
              data-testid="download-daily-pdf"
            >
              {downloading === 'daily' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Today&apos;s PDF
            </Button>
            <Button 
              onClick={downloadWeeklyPDF}
              disabled={downloading === 'weekly'}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
              data-testid="download-weekly-pdf"
            >
              {downloading === 'weekly' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Weekly PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {!data || (!data.news_items?.length && !pibData.length) ? (
          <Card className="border-dashed border-2"><CardContent className="p-8 sm:p-12 text-center">
            <Newspaper className="w-12 h-12 sm:w-16 sm:h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-bold text-slate-700">No Content for {selectedDate}</h3>
            <p className="text-sm sm:text-base text-slate-500 mt-2">Content is generated daily at 5:30 AM IST. Please check back later.</p>
          </CardContent></Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <TabsList className="inline-flex sm:grid sm:grid-cols-7 w-max sm:w-full max-w-4xl mx-auto h-auto min-w-full sm:min-w-0">
                <TabsTrigger value="summary" data-testid="tab-summary" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span>Summary</span></TabsTrigger>
                <TabsTrigger value="news" data-testid="tab-news" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><Newspaper className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span>News</span></TabsTrigger>
                <TabsTrigger value="pib" data-testid="tab-pib" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span>PIB</span></TabsTrigger>
                <TabsTrigger value="indepth" data-testid="tab-indepth" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span className="hidden sm:inline">In-Depth</span><span className="sm:hidden">Deep</span></TabsTrigger>
                <TabsTrigger value="places" data-testid="tab-places" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span>Places</span></TabsTrigger>
                <TabsTrigger value="personalities" data-testid="tab-people" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><User className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span>People</span></TabsTrigger>
                <TabsTrigger value="practice" data-testid="tab-practice" className="text-[11px] sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap"><Target className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1 hidden sm:inline" /><span className="hidden sm:inline">Practice</span><span className="sm:hidden">Quiz</span></TabsTrigger>
              </TabsList>
            </div>

            {/* SUMMARY TAB - Today's One-Liners & Key Stats */}
            <TabsContent value="summary" className="space-y-6">
              {/* Today's One-Liners */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-indigo-50/30">
                <CardContent className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Today&apos;s One-Liners
                    <Badge className="bg-orange-100 text-orange-700 border-0 text-xs ml-2">{data?.one_liners?.length || 0} points</Badge>
                  </h3>
                  <div className="space-y-3">
                    {(data?.one_liners || []).map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100 hover:border-indigo-200 hover:shadow-sm transition-all">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r ${getPaperColor(item.gs_paper)}`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{item.headline}</p>
                          {item.key_fact && (
                            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-500" />
                              {item.key_fact}
                            </p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0 bg-slate-50 text-slate-600">
                          {item.gs_paper}
                        </Badge>
                      </div>
                    ))}
                    {(!data?.one_liners || data.one_liners.length === 0) && (
                      <div className="text-center py-8 text-slate-400">
                        <Newspaper className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No one-liners available for this date</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Key Statistics */}
              {data?.key_stats && data.key_stats.length > 0 && (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-emerald-50/30">
                  <CardContent className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-emerald-500" />
                      Key Statistics & Data Points
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs ml-2">{data.key_stats.length} stats</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {data.key_stats.map((stat, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-emerald-100">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-700">{stat.stat}</p>
                            <p className="text-xs text-slate-400 mt-1">{stat.topic} • {stat.gs_paper}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Important Dates This Week */}
              {data?.important_dates && data.important_dates.length > 0 && (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-purple-50/30">
                  <CardContent className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-500" />
                      Important Dates This Week
                      <Badge className="bg-purple-100 text-purple-700 border-0 text-xs ml-2">{data.important_dates.length} dates</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {data.important_dates.map((dateItem, idx) => (
                        <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg border ${dateItem.is_today ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200' : 'bg-white border-purple-100'}`}>
                          <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center text-white ${dateItem.is_today ? 'bg-purple-600' : 'bg-purple-400'}`}>
                            <span className="text-xs font-medium">{dateItem.day.split(' ')[1]}</span>
                            <span className="text-lg font-bold leading-none">{dateItem.day.split(' ')[0]}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">{dateItem.name}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="outline" className="text-[10px] border-purple-200">{dateItem.gs_paper}</Badge>
                              <span className="text-xs text-slate-500">{dateItem.theme}</span>
                              {dateItem.is_today && <Badge className="bg-purple-600 text-white text-[10px]">TODAY</Badge>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Government Schemes in News */}
              {data?.govt_schemes && data.govt_schemes.length > 0 && (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-amber-50/30">
                  <CardContent className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Landmark className="w-5 h-5 text-amber-600" />
                      Government Schemes in News
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-xs ml-2">{data.govt_schemes.length} schemes</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {data.govt_schemes.map((scheme, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-amber-800">{scheme.name}</p>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-1">{scheme.mentioned_in}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] border-amber-200">{scheme.gs_paper}</Badge>
                              <span className="text-xs text-slate-400">{scheme.topic}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* NEWS TAB */}
            <TabsContent value="news" className="space-y-4">
              {/* Progressive Disclosure: Reading-mode toggle + progress strip */}
              <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-3 sm:p-4 mb-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap" data-testid="reading-mode-toggle">
                    <span className="text-[11px] sm:text-xs font-medium text-slate-500 mr-1">Reading mode:</span>
                    {[
                      { id: "quick", label: "Quick", emoji: "⚡", est: "~1 min/item" },
                      { id: "standard", label: "Standard", emoji: "📖", est: "~3 min/item" },
                      { id: "deep", label: "Deep dive", emoji: "🎓", est: "~8 min/item" },
                    ].map(m => (
                      <button
                        key={m.id}
                        data-testid={`reading-mode-${m.id}`}
                        onClick={() => setReadingMode(m.id)}
                        className={`text-[11px] sm:text-xs px-2.5 py-1.5 rounded-full border transition-all ${
                          readingMode === m.id
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
                        }`}
                      >
                        <span className="mr-1">{m.emoji}</span>{m.label}
                        <span className={`ml-1.5 ${readingMode === m.id ? "text-indigo-100" : "text-slate-400"} hidden sm:inline`}>· {m.est}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] sm:text-xs">
                    {streakCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-orange-600 font-medium" data-testid="reading-streak">
                        <Flame className="w-3.5 h-3.5" /> {streakCount}-day streak
                      </span>
                    )}
                    <span className="text-slate-500" data-testid="reading-progress">
                      <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5 text-emerald-500" />
                      {" "}{readItems.size} / {filteredNews.length} read
                    </span>
                  </div>
                </div>
                {filteredNews.length > 0 && (
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                      style={{ width: `${Math.min(100, (readItems.size / filteredNews.length) * 100)}%` }}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 mb-4 flex-wrap">
                {["all", "GS1", "GS2", "GS3", "GS4", "International", "Reports"].map(p => (
                  <Button key={p} variant={filterPaper === p ? "default" : "outline"} size="sm" onClick={() => setFilterPaper(p)}
                    className={filterPaper === p ? `bg-gradient-to-r ${getPaperColor(p)} border-0` : ""}>{p === "all" ? "All" : p}</Button>
                ))}
              </div>
              {filteredNews.length === 0 ? (
                <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-500">No news items for this filter</CardContent></Card>
              ) : filteredNews.map((item, idx) => (
                <Card key={item.id || idx} className={`transition-all ${
                  readItems.has(item.id)
                    ? "opacity-60 hover:opacity-100 bg-slate-50/50"
                    : "hover:shadow-lg"
                }`}>
                  <Collapsible open={expandedItems[item.id]} onOpenChange={() => toggleExpand(item.id)}>
                    <CollapsibleTrigger className="w-full p-4 cursor-pointer text-left">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {readItems.has(item.id) && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Read
                          </Badge>
                        )}
                        <Badge className={`bg-gradient-to-r ${getPaperColor(item.syllabus?.gs_paper)} text-white border-0 text-xs`}>{item.syllabus?.gs_paper}</Badge>
                        {item.category === "international" && (
                          <Badge className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white border-0 text-xs">International</Badge>
                        )}
                        {item.category && item.category !== "international" && (
                          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 capitalize">{item.category}</Badge>
                        )}
                        <span className="text-xs text-slate-500">{item.syllabus?.topic}</span>
                        {item.syllabus_topics && item.syllabus_topics.length > 0 && (
                          <Badge className="bg-violet-100 text-violet-700 text-[10px] ml-auto">
                            <BookOpen className="w-2.5 h-2.5 mr-0.5" />
                            {item.syllabus_topics.length} Syllabus Topics
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-bold text-slate-800 mb-2">{item.headline}</h3>
                      {/* Quick syllabus preview in collapsed view */}
                      {item.syllabus_topics && item.syllabus_topics.length > 0 && !expandedItems[item.id] && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.syllabus_topics.slice(0, 3).map((st, i) => (
                            <span key={i} className="text-[10px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full border border-violet-200">
                              {typeof st === 'string' ? st : st.topic}
                            </span>
                          ))}
                          {item.syllabus_topics.length > 3 && (
                            <span className="text-[10px] text-slate-400">+{item.syllabus_topics.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-3 sm:px-4 pb-4 border-t pt-4 space-y-3 sm:space-y-4">
                      {/* Context & Overview — collapsible (standard-level) */}
                      <DeepCollapsible
                        title="Context & Overview"
                        icon={BookOpen}
                        iconClass="text-indigo-500"
                        containerClass="bg-slate-50 rounded-xl p-3 sm:p-4"
                        headerClass="text-slate-700"
                        testId={`deep-section-context-${item.id}`}
                        defaultOpen={isOpen("standard")}
                      >
                        {renderStructured(item.context_background)}
                      </DeepCollapsible>

                      {/* What Is It? (Editorial items) — collapsible (standard-level) */}
                      {item.what_is_it && (
                        <DeepCollapsible
                          title="What Is It?"
                          icon={BookOpen}
                          iconClass="text-sky-600"
                          containerClass="bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl p-3 sm:p-4 border border-sky-200"
                          headerClass="text-sky-800"
                          testId={`deep-section-what-${item.id}`}
                          defaultOpen={isOpen("standard")}
                        >
                          <p className="text-xs sm:text-sm text-sky-700 leading-relaxed">{safeText(item.what_is_it)}</p>
                        </DeepCollapsible>
                      )}

                      {/* DEEP: Constitutional & Legal Basis (Editorial items) — collapsed by default */}
                      {item.constitutional_and_legal_basis && (
                        <DeepCollapsible
                          title="Constitutional & Legal Basis"
                          icon={Scale}
                          iconClass="text-rose-600"
                          containerClass="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-3 sm:p-4 border border-rose-200"
                          headerClass="text-rose-800"
                          testId={`deep-section-legal-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          {renderStructured(item.constitutional_and_legal_basis, "text-xs sm:text-sm text-rose-700")}
                        </DeepCollapsible>
                      )}

                      {/* Syllabus Connection — collapsible (deep-level, since it's heavy with chips) */}
                      {item.background_linkage && (
                        <DeepCollapsible
                          title="Syllabus Connection"
                          icon={GraduationCap}
                          iconClass="text-purple-500"
                          containerClass="bg-white rounded-xl p-3 sm:p-4 border border-slate-200"
                          headerClass="text-slate-700"
                          testId={`deep-section-syllabus-conn-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          {renderStructured(item.background_linkage || item.syllabus_connection, "text-xs sm:text-sm text-slate-600")}
                          
                          {/* Related Articles, Acts, Institutions */}
                          <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3">
                            {item.related_articles?.map((art, i) => (
                              <Badge key={`art-${i}`} className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] sm:text-xs">
                                <Scale className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />{art}
                              </Badge>
                            ))}
                            {item.related_acts?.map((act, i) => (
                              <Badge key={`act-${i}`} className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] sm:text-xs">
                                <FileText className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />{safeText(act)}
                              </Badge>
                            ))}
                            {item.related_institutions?.map((inst, i) => (
                              <Badge key={`inst-${i}`} className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] sm:text-xs">
                                <Building2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />{safeText(inst)}
                              </Badge>
                            ))}
                          </div>
                        </DeepCollapsible>
                      )}

                      {/* Summary Points */}
                      {item.key_points?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 text-sm sm:text-base">Summary Points</h4>
                          <ul className="space-y-1">{item.key_points.map((p, i) => <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-600"><CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{safeText(p)}</li>)}</ul>
                        </div>
                      )}

                      {/* Exam-Ready Facts */}
                      {item.key_facts?.length > 0 && (
                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 sm:p-4 border border-emerald-200">
                          <h4 className="font-semibold text-emerald-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                            <Flame className="w-4 h-4 text-emerald-600" />
                            Exam-Ready Facts
                          </h4>
                          <ul className="space-y-1.5 sm:space-y-2">
                            {item.key_facts.map((fact, i) => (
                              <li key={i} className="text-xs sm:text-sm text-emerald-700 flex items-start gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {safeText(fact)}                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* DEEP: Key Data & Facts — collapsed by default */}
                      {Array.isArray(item.key_data_and_facts) && item.key_data_and_facts.length > 0 && (
                        <DeepCollapsible
                          title="Key Data & Statistics"
                          icon={Target}
                          iconClass="text-amber-400"
                          containerClass="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-3 sm:p-4 border border-slate-700"
                          headerClass="text-white"
                          testId={`deep-section-data-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          <ul className="space-y-1.5 sm:space-y-2">
                            {item.key_data_and_facts.map((d, i) => (
                              <li key={i} className="text-xs sm:text-sm text-slate-200 flex items-start gap-2">
                                <span className="text-amber-400 font-mono font-bold mt-0.5 flex-shrink-0">{i+1}.</span>
                                {safeText(d)}
                              </li>
                            ))}
                          </ul>
                        </DeepCollapsible>
                      )}

                      {/* DEEP: Analysis & Significance — collapsed by default */}
                      {item.analysis_and_significance && (
                        <DeepCollapsible
                          title="Analysis & Significance"
                          icon={Microscope}
                          iconClass="text-teal-600"
                          containerClass="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-3 sm:p-4 border border-teal-200"
                          headerClass="text-teal-800"
                          testId={`deep-section-analysis-${item.id}`}
                          defaultOpen={isOpen("standard")}
                        >
                          {renderStructured(item.analysis_and_significance, "text-xs sm:text-sm text-teal-700")}
                        </DeepCollapsible>
                      )}

                      {/* PYQ Connections — collapsed by default */}
                      {Array.isArray(item.pyq_connections) && item.pyq_connections.length > 0 && (
                        <DeepCollapsible
                          title="Previous Year Connections"
                          icon={Clock}
                          iconClass="text-amber-600"
                          containerClass="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 sm:p-4 border border-amber-200"
                          headerClass="text-amber-800"
                          testId={`deep-section-pyq-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          <ul className="space-y-1.5 sm:space-y-2">
                            {item.pyq_connections.map((pyq, i) => (
                              <li key={i} className="text-xs sm:text-sm text-amber-700 flex items-start gap-2">
                                <span className="text-amber-500 font-bold mt-0.5 flex-shrink-0">PYQ</span>
                                {safeText(pyq)}
                              </li>
                            ))}
                          </ul>
                        </DeepCollapsible>
                      )}

                      {/* Mains Answer Framework — collapsed by default */}
                      {item.mains_answer_framework && (
                        <DeepCollapsible
                          title="Mains Answer Framework"
                          icon={FileText}
                          iconClass="text-blue-600"
                          containerClass="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-200"
                          headerClass="text-blue-800"
                          testId={`deep-section-mains-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          {renderStructured(item.mains_answer_framework, "text-xs sm:text-sm text-blue-700")}
                        </DeepCollapsible>
                      )}

                      {/* Syllabus Mapping — collapsible (deep) */}
                      <DeepCollapsible
                        title="Syllabus Mapping"
                        icon={GraduationCap}
                        iconClass="text-indigo-700"
                        containerClass="bg-indigo-50 rounded-xl p-3 sm:p-4"
                        headerClass="text-indigo-800"
                        testId={`deep-section-syllabus-map-${item.id}`}
                        defaultOpen={isOpen("deep")}
                      >
                        <p className="text-xs sm:text-sm text-indigo-700">{item.syllabus?.gs_paper} &gt; {item.syllabus?.section} &gt; {item.syllabus?.topic} &gt; {item.syllabus?.sub_topic}</p>
                      </DeepCollapsible>

                      {/* UPSC Syllabus Topics — collapsible (deep) */}
                      {item.syllabus_topics && item.syllabus_topics.length > 0 && (
                        <DeepCollapsible
                          title={`UPSC Syllabus Relevance · ${item.syllabus_topics.length} topics`}
                          icon={BookOpen}
                          iconClass="text-violet-600"
                          containerClass="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-3 sm:p-4 border border-violet-200"
                          headerClass="text-violet-800"
                          testId={`deep-section-upsc-relevance-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          <div className="space-y-3">
                            {item.syllabus_topics.map((st, i) => {
                              // Handle both string and object formats
                              const isString = typeof st === 'string';
                              const paper = isString ? (item.gs_paper || item.syllabus?.gs_paper || '') : st.paper;
                              const topic = isString ? st : st.topic;
                              const subtopic = isString ? null : st.subtopic;
                              return (
                              <div key={i} className="bg-white rounded-lg p-3 border border-violet-100 shadow-sm">
                                <div className="flex items-start gap-2 mb-2">
                                  <Badge className={`text-[10px] sm:text-xs font-medium ${
                                    paper?.includes('GS1') ? 'bg-orange-100 text-orange-700' :
                                    paper?.includes('GS2') ? 'bg-blue-100 text-blue-700' :
                                    paper?.includes('GS3') ? 'bg-emerald-100 text-emerald-700' :
                                    paper?.includes('GS4') ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {paper || 'GS'}
                                  </Badge>
                                </div>
                                <p className="text-xs sm:text-sm font-medium text-violet-900">{topic}</p>
                                {subtopic && (
                                  <p className="text-[10px] sm:text-xs text-violet-600 mt-0.5">{subtopic}</p>
                                )}
                                {!isString && st.relevance && (
                                  <p className="text-[10px] sm:text-xs text-slate-600 mt-2 italic border-l-2 border-violet-200 pl-2">
                                    &ldquo;{st.relevance}&rdquo;
                                  </p>
                                )}
                              </div>
                            );
                            })}
                          </div>
                        </DeepCollapsible>
                      )}

                      {/* Why UPSC Cares — collapsible (standard) */}
                      <DeepCollapsible
                        title="Why UPSC Cares"
                        icon={AlertCircle}
                        iconClass="text-amber-500"
                        containerClass="bg-white rounded-xl p-3 sm:p-4 border border-slate-200"
                        headerClass="text-slate-800"
                        testId={`deep-section-why-upsc-${item.id}`}
                        defaultOpen={isOpen("standard")}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                          {/* Prelims Relevance */}
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-200">
                            <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <Target className="w-4 h-4" />
                              Prelims Relevance
                            </h5>
                            {item.prelims_focus?.length > 0 ? (
                              <ul className="space-y-1 sm:space-y-1.5">
                                {item.prelims_focus.map((f, i) => (
                                  <li key={i} className="text-xs sm:text-sm text-blue-700 flex items-start gap-2">
                                    <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 mt-1 text-blue-500 flex-shrink-0" />
                                    {f}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs sm:text-sm text-blue-600">Important for Prelims</p>
                            )}
                            {item.prelims_relevance?.length > 0 && (
                              <ul className="space-y-1 sm:space-y-1.5 mt-2 pt-2 border-t border-blue-200">
                                {item.prelims_relevance.map((r, i) => (
                                  <li key={i} className="text-xs sm:text-sm text-blue-600 flex items-start gap-2">
                                    <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mt-1 text-blue-400 flex-shrink-0" />
                                    {safeText(r)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          
                          {/* Mains Relevance */}
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 sm:p-4 border border-purple-200">
                            <h5 className="font-medium text-purple-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <FileText className="w-4 h-4" />
                              Mains Relevance
                            </h5>
                            {item.mains_analysis ? (
                              renderStructured(item.mains_analysis, "text-xs sm:text-sm text-purple-700")
                            ) : (
                              <p className="text-xs sm:text-sm text-purple-600">Important for Mains</p>
                            )}
                            {Array.isArray(item.mains_relevance) && item.mains_relevance.length > 0 ? (
                              <ul className="space-y-1 sm:space-y-1.5 mt-2 pt-2 border-t border-purple-200">
                                {item.mains_relevance.map((r, i) => (
                                  <li key={i} className="text-xs sm:text-sm text-purple-600 flex items-start gap-2">
                                    <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mt-1 text-purple-400 flex-shrink-0" />
                                    {safeText(r)}
                                  </li>
                                ))}
                              </ul>
                            ) : item.mains_relevance ? (
                              <p className="text-xs sm:text-sm text-purple-600 mt-2 pt-2 border-t border-purple-200 leading-relaxed">{safeText(item.mains_relevance)}</p>
                            ) : null}
                          </div>
                        </div>
                      </DeepCollapsible>

                      {/* Mains Dimensions — collapsible (deep) */}
                      {item.mains_dimensions?.length > 0 && (
                        <DeepCollapsible
                          title="Mains Dimensions"
                          icon={Scale}
                          iconClass="text-teal-600"
                          containerClass="bg-teal-50 rounded-xl p-3 sm:p-4 border border-teal-200"
                          headerClass="text-teal-800"
                          testId={`deep-section-mains-dim-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {item.mains_dimensions.map((d, i) => { const dimText = safeText(d); const Icon = getDimensionIcon(dimText); return <Badge key={i} variant="outline" className="bg-white text-teal-700 border-teal-200 text-[10px] sm:text-xs"><Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />{dimText}</Badge>; })}
                          </div>
                        </DeepCollapsible>
                      )}

                      {/* PYQ Intelligence — collapsible (deep) */}
                      {(item.pyq_links?.length > 0 || item.pyq_themes?.length > 0) && (
                        <DeepCollapsible
                          title="PYQ Intelligence"
                          icon={Clock}
                          iconClass="text-orange-600"
                          containerClass="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-3 sm:p-4 border border-orange-200"
                          headerClass="text-orange-800"
                          testId={`deep-section-pyq-intel-${item.id}`}
                          defaultOpen={isOpen("deep")}
                        >
                          {item.pyq_themes?.length > 0 && (
                            <ul className="space-y-1 sm:space-y-1.5 mb-3">
                              {item.pyq_themes.map((theme, i) => (
                                <li key={i} className="text-xs sm:text-sm text-orange-700 flex items-start gap-2">
                                  <span className="text-orange-500">•</span>
                                  {safeText(theme)}
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.pyq_links?.length > 0 && (
                            <div className="space-y-2">
                              {item.pyq_links.map((pyq, i) => (
                                <div key={i} className="bg-white rounded-lg p-2 sm:p-3 border border-orange-200">
                                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                                    <Badge className="bg-orange-100 text-orange-700 text-[10px] sm:text-xs">{pyq.year}</Badge>
                                    <Badge variant="outline" className="text-[10px] sm:text-xs">{pyq.paper}</Badge>
                                  </div>
                                  <p className="text-[10px] sm:text-xs text-orange-800">{pyq.question_snippet}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </DeepCollapsible>
                      )}

                      {/* Mark as Read + Download — bottom CTA strip */}
                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-200 flex-wrap">
                        <Button
                          variant={readItems.has(item.id) ? "default" : "outline"}
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); toggleRead(item.id); }}
                          className={`text-[11px] sm:text-xs ${
                            readItems.has(item.id)
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                              : "bg-white hover:bg-emerald-50 text-emerald-700 border-emerald-300"
                          }`}
                          data-testid={`mark-read-${item.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          {readItems.has(item.id) ? "Marked as read" : "Mark as read"}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); downloadItemPDF(item); }}
                          className="text-[10px] sm:text-xs bg-white hover:bg-slate-50"
                          data-testid={`download-item-${item.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download as PDF
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))}
            </TabsContent>

            {/* PIB TAB */}
            <TabsContent value="pib" className="space-y-4">
              {pibLoading ? (
                <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" /></div>
              ) : pibData.length === 0 ? (
                <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  No PIB releases for this date
                </CardContent></Card>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-cyan-500" />
                      Press Information Bureau ({pibData.length} releases)
                    </h3>
                  </div>
                  {pibData.map((release, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow border-l-4 border-l-cyan-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-cyan-100 text-cyan-800">{release.ministry || 'Government'}</Badge>
                              {release.relevance === 'HIGH' && <Badge className="bg-red-100 text-red-800">High Relevance</Badge>}
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">{release.title}</h3>
                            <p className="text-sm text-slate-600 mb-3">{release.summary || release.content?.substring(0, 200)}...</p>
                            {release.upsc_relevance && (
                              <div className="bg-indigo-50 rounded-lg p-3">
                                <p className="text-xs font-semibold text-indigo-700 mb-1">UPSC Relevance</p>
                                <p className="text-xs text-indigo-600">{release.upsc_relevance}</p>
                              </div>
                            )}
                          </div>
                          {release.url && (
                            <a href={release.url} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700">
                              <ChevronRight className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* IN-DEPTH TAB */}
            <TabsContent value="indepth" className="space-y-8">
              {data?.in_depth_analyses?.length === 0 ? (
                <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-500">No in-depth analyses available</CardContent></Card>
              ) : data?.in_depth_analyses?.map((a, idx) => (
                <InDepthArticle key={a.id || idx} article={a} />
              ))}
            </TabsContent>

            {/* PLACES TAB */}
            <TabsContent value="places" className="grid md:grid-cols-2 gap-4">
              {(data?.places_in_news || data?.places_items || []).length === 0 ? (
                <Card className="border-dashed col-span-2"><CardContent className="p-8 text-center text-slate-500">No places in news</CardContent></Card>
              ) : (data?.places_in_news || data?.places_items || []).map((p, idx) => (
                <Card key={p.id || idx} className="hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-slate-800">{p.name}</h3><Badge variant="outline" className="text-xs">{p.country}</Badge></div>
                        <p className="text-xs text-slate-500 mb-2">{p.region}</p>
                        <div className="bg-slate-50 rounded-lg p-3 mb-2"><p className="text-xs font-semibold text-slate-700 mb-1">Why in News</p><p className="text-xs text-slate-600">{p.why_in_news}</p></div>
                        <div className="bg-emerald-50 rounded-lg p-3 mb-2"><p className="text-xs font-semibold text-emerald-700 mb-1">Strategic Importance</p><p className="text-xs text-emerald-600">{p.strategic_importance}</p></div>
                        <div className="bg-indigo-50 rounded-lg p-3"><p className="text-xs font-semibold text-indigo-700 mb-1">UPSC Relevance</p><p className="text-xs text-indigo-600">{p.upsc_relevance}</p></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* PERSONALITIES TAB */}
            <TabsContent value="personalities" className="grid md:grid-cols-2 gap-4">
              {(data?.personalities_in_news || data?.personalities_items || []).length === 0 ? (
                <Card className="border-dashed col-span-2"><CardContent className="p-8 text-center text-slate-500">No personalities in news</CardContent></Card>
              ) : (data?.personalities_in_news || data?.personalities_items || []).map((p, idx) => (
                <Card key={p.id || idx} className="hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center"><User className="w-5 h-5 text-white" /></div>
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-800">{p.name}</h3>
                        <p className="text-sm text-slate-600">{p.designation}</p>
                        <p className="text-xs text-slate-500 mb-2">{p.organization} • {p.country}</p>
                        <div className="bg-slate-50 rounded-lg p-3 mb-2"><p className="text-xs font-semibold text-slate-700 mb-1">Why in News</p><p className="text-xs text-slate-600">{p.why_in_news}</p></div>
                        <div className="bg-indigo-50 rounded-lg p-3"><p className="text-xs font-semibold text-indigo-700 mb-1">UPSC Relevance</p><p className="text-xs text-indigo-600">{p.upsc_relevance}</p></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* PRACTICE TAB */}
            <TabsContent value="practice" className="space-y-6">
              <PracticeSection newsItems={data?.news_items || []} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

const PracticeSection = ({ newsItems }) => {
  const [mcqIdx, setMcqIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showAns, setShowAns] = useState(false);
  const mcqs = newsItems.filter(i => i.prelims_mcq).map(i => ({ ...i.prelims_mcq, topic: i.headline }));
  const mains = newsItems.filter(i => i.mains_question).map(i => ({ ...i.mains_question, topic: i.headline }));
  const curr = mcqs[mcqIdx];
  const next = () => { setSelected(null); setShowAns(false); setMcqIdx((mcqIdx + 1) % mcqs.length); };

  return (
    <div className="space-y-6">
      <Card className="shadow-lg"><CardContent className="p-6">
        <h3 className="text-lg font-bold mb-4"><Target className="w-5 h-5 inline text-blue-500 mr-2" />Prelims MCQs ({mcqs.length})</h3>
        {curr ? (<div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-2">Q{mcqIdx + 1}/{mcqs.length}</p>
            {/*
              Question may inline numbered statements (e.g. "...is/are correct?
              1. Foo... 2. Bar... 3. Baz"). Split them onto separate lines for
              UPSC-style readability.

              Pattern requires:
                - the marker is a 1-2 digit number (so "2026." in a date doesn't match)
                - followed by ". " and then a capital letter (real statement starts)
              This avoids splitting "April 2026. 2." in the middle of a date.
            */}
            {(() => {
              const q = curr.question || "";
              const parts = q.split(/\s+(?=[1-9]\d?\.\s+[A-Z])/);
              if (parts.length <= 1) {
                return <p className="font-medium whitespace-pre-line">{q}</p>;
              }
              return (
                <div className="font-medium space-y-1">
                  <p>{parts[0]}</p>
                  <ol className="list-none pl-2 space-y-1">
                    {parts.slice(1).map((line, k) => (
                      <li key={k}>{line.trim()}</li>
                    ))}
                  </ol>
                </div>
              );
            })()}
          </div>
          <div className="space-y-2">{curr.options?.map((o, i) => {
            /*
              The generator sometimes returns options already prefixed
              ("A) National Education Policy 1986") and the UI was adding
              its own "A. " prefix → "A. A) National Education Policy 1986".
              Strip any leading option-letter marker before display.
            */
            const cleaned = String(o ?? "").replace(/^\s*[\(\[]?[A-Da-d][\)\].:\-]\s*/, "").trim();
            return (
              <button key={i} onClick={() => { setSelected(i); setShowAns(true); }} disabled={showAns}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${showAns ? (i === curr.correct_answer ? 'border-emerald-500 bg-emerald-50' : i === selected ? 'border-red-500 bg-red-50' : 'border-slate-200') : selected === i ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300'}`}>
                {String.fromCharCode(65 + i)}. {cleaned}
              </button>
            );
          })}</div>
          {showAns && <div className={`rounded-xl p-4 ${selected === curr.correct_answer ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <p className={`font-semibold mb-2 ${selected === curr.correct_answer ? 'text-emerald-700' : 'text-amber-700'}`}>{selected === curr.correct_answer ? '✓ Correct!' : '✗ Incorrect'}</p>
            <p className="text-sm">{curr.explanation}</p>
          </div>}
          <div className="flex justify-between"><Badge variant="outline">{curr.difficulty}</Badge><Button onClick={next} disabled={!showAns}>Next <ChevronRight className="w-4 h-4 ml-1" /></Button></div>
        </div>) : <p className="text-slate-500 text-center py-8">No MCQs available for today</p>}
      </CardContent></Card>

      <Card className="shadow-lg"><CardContent className="p-6">
        <h3 className="text-lg font-bold mb-4"><FileText className="w-5 h-5 inline text-purple-500 mr-2" />Mains Questions</h3>
        {mains.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No Mains questions available</p>
        ) : (
          <div className="space-y-4">{mains.slice(0, 5).map((q, idx) => (
            <div key={idx} className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Badge className="bg-purple-100 text-purple-800">{q.marks}M</Badge></div>
              <p className="font-medium mb-3">{q.question}</p>
              <Collapsible><CollapsibleTrigger className="text-sm text-indigo-600 flex items-center gap-1">View Approach <ChevronDown className="w-4 h-4" /></CollapsibleTrigger>
                <CollapsibleContent className="mt-3 bg-white rounded-lg p-3">
                  <p className="text-xs font-semibold mb-2">Approach Hints:</p>
                  <ul className="space-y-1">{q.approach_hints?.map((h, i) => <li key={i} className="text-xs text-slate-600">{i + 1}. {h}</li>)}</ul>
                  {q.key_points?.length > 0 && (
                    <>
                      <p className="text-xs font-semibold mt-3 mb-2">Key Points:</p>
                      <ul className="space-y-1">{q.key_points.map((p, i) => <li key={i} className="text-xs text-slate-600 flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5" />{p}</li>)}</ul>
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}</div>
        )}
      </CardContent></Card>
    </div>
  );
};

export default DailyIntelligenceV2Page;
